import { BusinessModel } from 'enums/businessModel';
import { SupportedDemandType } from 'enums/SupportedDemandType';
import { VehicleType } from 'enums/vehicleTypes';
import { useForm } from 'hooks/useForm';
import { useTranslatedNavigation } from 'hooks/useTranslatedNavigation';
import useTranslation from 'next-translate/useTranslation';
import { useRouter } from 'next/router';
import {
  FunctionComponent,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { toast } from 'react-toastify';
import { dateService } from 'services/DateService';
import { demandService } from 'services/DemandService';
import {
  CalculatePricing,
  DemandResponseDemand,
  DemandSubmitType,
  SelectedExtraUnits,
  VehicleBasedDailyWeeklyDemandState,
} from 'types/demand.type';
import { DropdownOption } from 'types/types';
import {
  DailyWeeklyRentalInfo,
  ExtraWithUnit,
  ReservedTime,
} from 'types/vehicle.type';
import { vehicleBasedDWValidation } from 'validation/demand.validation';
import { useQueryParams } from 'hooks/useQueryParams';
import { useAuthCheck } from 'hooks/useAuthCheck';
import PriceWidgetCTAButton from './PriceWidgetCTAButton';
import { usePriceWidgetDWCalculateAmounts } from 'hooks/priceWidget/usePriceWidgetDWCalculateAmounts';
import { usePriceWidgetDWPeopleLimits } from 'hooks/priceWidget/usePriceWidgetDWPeopleLimits';
import classNames from 'classnames';
import BStepperInput from 'components/Form/BStepperInput';
import BDateRangePicker from 'components/Form/BDateRangePicker';
import { usePriceWidgetExtraSections } from 'hooks/priceWidget/usePriceWidgetExtraSections';
import ModalWithActivator from 'components/common/ModalWithActivator';
import PriceWidgetExtras from './PriceWidgetExtras';
import BTabs from 'components/common/BTabs';
import PriceWidgetExtrasEmptyView from './PriceWidgetExtrasEmptyView';
import { usePriceWidgetCalculateDWDuration } from 'hooks/priceWidget/usePriceWidgetCalculateDWDuration';
import { SupportedDailyWeeklyRentalTypes } from 'enums/supportedRentalTypes';
import { usePriceWidgetDWAvailableDays } from 'hooks/priceWidget/usePriceWidgetDWAvailableDays';
import { usePriceWidgetDWReservedTimes } from 'hooks/priceWidget/usePriceWidgetDWReservedTimes';
import { usePriceWidgetDWClosestAvailability } from 'hooks/priceWidget/usePriceWidgetDWClosestAvailability';
import BSwitch from 'components/Form/BSwitch';
import { Currency } from 'enums/currency';
import { analyticsService } from 'services/AnalyticsService';
import { VehiclePriceUnit } from 'enums/vehiclePriceUnit';
import { usePriceWidgetDWGetOffsetDays } from 'hooks/priceWidget/usePriceWidgetDWGetOffsetDays';
import { Dayjs } from 'dayjs';
import BTextArea from 'components/Form/BTextArea';
import BButton from 'components/common/BButton';
import BSpinnerInline from 'components/common/BSpinnerInline';
import { useRemoteConfigState } from 'states/useRemoteConfigState';
import { getLocalePrefix } from 'utils/getLocalePrefix';
import { useLocale } from 'hooks/useLocale';

interface PriceWidgetDailyWeeklyFormProps {
  supportedServices: ExtraWithUnit[];
  cruisingCapacity: number;
  overnightCapacity: number;
  vehicleId: number;
  setPricing: (val: CalculatePricing.MappedResponseWithDuration) => void;
  rentalInfo: DailyWeeklyRentalInfo;
  reservedTimes: ReservedTime[];
  harbors: DropdownOption<number>[];
  originHarborId: number;
  supportedDemandTypes: SupportedDemandType[];
  pricing?: CalculatePricing.MappedResponseWithDuration;
  isCalculateAmountRequestSent: boolean;
  setIsCalculateAmountRequestSent: (val: boolean) => void;
  businessModel: BusinessModel;
  vehicleName: string;
  vehicleType: VehicleType;
  regionTRName: string;
  isCalcutePricingRequestOngoing: boolean;
  setIsCalcutePricingRequestOngoing: (val: boolean) => void;
  submitType?: DemandSubmitType;
  demandId?: number;
  submitCallback?: () => void;
  demandData?: DemandResponseDemand;
}

const PriceWidgetDailyWeeklyForm: FunctionComponent<
  PriceWidgetDailyWeeklyFormProps
> = ({
  supportedServices,
  cruisingCapacity,
  overnightCapacity,
  vehicleId,
  setPricing,
  rentalInfo,
  reservedTimes,
  originHarborId,
  supportedDemandTypes,
  pricing,
  harbors,
  isCalculateAmountRequestSent,
  setIsCalculateAmountRequestSent,
  vehicleName,
  vehicleType,
  regionTRName,
  isCalcutePricingRequestOngoing,
  setIsCalcutePricingRequestOngoing,
  submitType = 'default',
  demandId = null,
  submitCallback,
  demandData,
}) => {
  const dayjs = dateService.getDayjs();
  const router = useRouter();
  const { translateAndNavigate } = useTranslatedNavigation();
  const { t } = useTranslation('vehicle');
  const { locale } = useLocale();
  const { addQueryParam, getQueryParam } = useQueryParams();
  const { checkLoggedIn } = useAuthCheck();
  const [message, setMessage] = useState<string>('');
  const { getConfig } = useRemoteConfigState();
  const isMessagingActive = getConfig('MESSAGING_ENABLED', true);

  const {
    calculateDWDemandVariant,
    formatDatesToCorrectHours,
    getDemandDWDuration,
  } = usePriceWidgetCalculateDWDuration(rentalInfo);

  const [isDemandRequestOngoing, setIsDemandRequestOngoing] =
    useState<boolean>(false);

  const validateForm = (
    state: VehicleBasedDailyWeeklyDemandState,
    showError: boolean
  ): Promise<boolean> => {
    return vehicleBasedDWValidation(
      state,
      0, //MIN_DURATION,
      cruisingCapacity,
      overnightCapacity,
      t,
      showError
    );
  };

  const { debouncedHandleCalculateAmounts } = usePriceWidgetDWCalculateAmounts(
    vehicleId,
    supportedServices,
    setPricing,
    supportedDemandTypes,
    validateForm,
    setIsCalculateAmountRequestSent,
    setIsCalcutePricingRequestOngoing,
    pricing,
    rentalInfo
  );

  const generateSelectedExtraUnits = (supportedExtras: ExtraWithUnit[]) => {
    return supportedExtras.reduce(
      (acc, item) => ({
        ...acc,
        [item.id]: null,
      }),
      {} as SelectedExtraUnits
    );
  };

  const getDefaultDemandVariant = () => {
    return rentalInfo.supportedRentalTypes.includes(
      SupportedDailyWeeklyRentalTypes.DAILY
    )
      ? SupportedDailyWeeklyRentalTypes.DAILY
      : rentalInfo.supportedRentalTypes.includes(
          SupportedDailyWeeklyRentalTypes.WEEKLY
        )
      ? SupportedDailyWeeklyRentalTypes.WEEKLY
      : SupportedDailyWeeklyRentalTypes.HALF_DAY;
  };

  const resetDay = (day: Dayjs) => {
    return day.hour(0).minute(0).second(0).millisecond(0).toDate();
  };

  const getDefaultState =
    useCallback((): VehicleBasedDailyWeeklyDemandState => {
      const isHalfDay = getQueryParam('halfDay') === 'true';
      let demandVariant: SupportedDailyWeeklyRentalTypes =
        SupportedDailyWeeklyRentalTypes.DAILY;
      if (isHalfDay) {
        demandVariant = SupportedDailyWeeklyRentalTypes.HALF_DAY;
      } else {
        demandVariant = getDefaultDemandVariant();
      }

      if (demandData) {
        demandVariant = demandData.dwVariant;
      }

      const endDateOffset: number = {
        [SupportedDailyWeeklyRentalTypes.DAILY]: 1,
        [SupportedDailyWeeklyRentalTypes.WEEKLY]: 7,
        [SupportedDailyWeeklyRentalTypes.HALF_DAY]: 0,
      }[demandVariant];

      const queryParamState = {
        startDate: getQueryParam('startDate') as string | undefined,
        endDate: getQueryParam('endDate') as string | undefined,
        numberOfPeople: getQueryParam('numberOfPeople') as string | undefined,
        selectedServices: getQueryParam('selectedServices') as
          | string[]
          | undefined,
      };

      let startDate = resetDay(
        dayjs(
          queryParamState.startDate ? +queryParamState.startDate : undefined
        )
      );

      if (demandData?.startTime) {
        startDate = resetDay(dayjs(demandData?.startTime));
      }

      let endDate = resetDay(
        queryParamState.endDate
          ? dayjs(+queryParamState.endDate)
          : dayjs(startDate).add(endDateOffset, 'days')
      );

      if (demandData?.endTime) {
        endDate = resetDay(dayjs(demandData?.endTime));
      }

      let harbor = `${originHarborId}`;
      let endHarbor = `${originHarborId}`;

      if (demandData?.startHarbor && demandData?.finishHarbor) {
        harbor =
          '' + harbors.find((h) => h.label === demandData?.startHarbor)?.value;
        endHarbor =
          '' + harbors.find((h) => h.label === demandData?.finishHarbor)?.value;
      }

      const maxLimit =
        demandVariant === SupportedDailyWeeklyRentalTypes.HALF_DAY
          ? cruisingCapacity
          : overnightCapacity;

      let numberOfPeople =
        queryParamState.numberOfPeople &&
        +queryParamState.numberOfPeople > 0 &&
        +queryParamState.numberOfPeople <= maxLimit
          ? queryParamState.numberOfPeople
          : '0';

      if (demandData?.passengerCount) {
        numberOfPeople = '' + demandData?.passengerCount;
      }

      const selectedServices: number[] =
        demandData?.services.map((s) => s.id) || [];

      if (
        queryParamState.selectedServices &&
        queryParamState.selectedServices.length > 0
      ) {
        queryParamState.selectedServices.forEach((s) => {
          if (supportedServices.find((ss) => ss.id === +s)) {
            selectedServices.push(+s);
          }
        });
      }

      if (demandData?.services) {
        demandData?.services.forEach((s) => {
          if (supportedServices.find((ss) => ss.id === +s)) {
            selectedServices.push(+s);
          }
        });
      }

      return {
        startDate,
        endDate,
        harbor,
        endHarbor,
        numberOfPeople,
        selectedServices,
        selectedServicesUnits: generateSelectedExtraUnits(supportedServices),
        demandVariant,
      };
    }, []);

  const dispatchInterceptor = (state: VehicleBasedDailyWeeklyDemandState) => {
    debouncedHandleCalculateAmounts(state);
    // setStoredState(state);

    const strippedState = {
      startDate: state.startDate?.getTime(),
      endDate: state.endDate?.getTime(),
      numberOfPeople: state.numberOfPeople,
      selectedServices: state.selectedServices,
      halfDay: state.demandVariant === SupportedDailyWeeklyRentalTypes.HALF_DAY,
    };
    submitType !== 'message-update' && addQueryParam(strippedState);
  };

  const [state, dispatch] = useForm(
    {
      ...getDefaultState(),
      // ...storedState,
    },
    dispatchInterceptor
  );

  useEffect(() => {
    debouncedHandleCalculateAmounts(state);
  }, []);

  const { mappedReservedTimes } = usePriceWidgetDWReservedTimes({
    state,
    reservedTimes,
    rentalInfo,
  });

  const { getDisabledDays, disableDatesBeforeToday } =
    usePriceWidgetDWAvailableDays(rentalInfo, state);

  const { isDateRangeAvailable } = usePriceWidgetDWClosestAvailability({
    state,
    dispatch,
    mappedReservedTimes,
    getDisabledDays,
    disableDatesBeforeToday,
  });

  const { MAX_PEOPLE, MIN_PEOPLE } = usePriceWidgetDWPeopleLimits(
    state,
    dispatch,
    cruisingCapacity,
    overnightCapacity
  );

  const { extraTabs } = usePriceWidgetExtraSections(
    state,
    dispatch,
    [],
    [],
    supportedServices,
    0
  );

  const { offsetDays } = usePriceWidgetDWGetOffsetDays({ state, rentalInfo });

  const doesSupportHalfDayRental = useMemo(() => {
    return rentalInfo.supportedRentalTypes.includes(
      SupportedDailyWeeklyRentalTypes.HALF_DAY
    );
  }, [rentalInfo.supportedRentalTypes]);

  const doesSupportOnlyHalfDayRental = useMemo(() => {
    return (
      doesSupportHalfDayRental && rentalInfo.supportedRentalTypes.length === 1
    );
  }, [rentalInfo.supportedRentalTypes, doesSupportHalfDayRental]);

  const clearUnsupportedExtras = () => {
    const stateServices = state['selectedServices'];

    const selectedServices = stateServices.filter((service) =>
      supportedServices.some(
        (supportedService) => supportedService.id === service
      )
    );

    return {
      selectedServices,
    };
  };

  const onSubmit = async () => {
    if (!checkLoggedIn()) {
      return;
    }

    const clearedExtras = clearUnsupportedExtras();

    const demandVariant = calculateDWDemandVariant(state);
    const filteredState: VehicleBasedDailyWeeklyDemandState = {
      ...state,
      demandVariant,
      selectedServices: clearedExtras.selectedServices,
      ...formatDatesToCorrectHours(state),
    };

    const duration = getDemandDWDuration(filteredState);

    if (
      filteredState.demandVariant !== SupportedDailyWeeklyRentalTypes.HALF_DAY
    ) {
      let calculatedDuration = duration;
      if (
        filteredState.demandVariant === SupportedDailyWeeklyRentalTypes.WEEKLY
      ) {
        calculatedDuration *= 7;
      }
      if (calculatedDuration < rentalInfo.minimumRentDuration) {
        toast.error(
          t('priceWidget.form.errors.lessThanMinimumRentDuration', {
            count: rentalInfo.minimumRentDuration,
          }),
          {
            toastId: 'lessThanMinimumRentDuration',
          }
        );
        return false;
      }
    }

    const isValid = await validateForm(filteredState, true);
    if (!isValid) return false;

    if (
      state.startDate &&
      state.endDate &&
      !isDateRangeAvailable({
        startDate: state.startDate,
        endDate: state.endDate,
      })
    ) {
      toast.error(t('priceWidget.form.errors.dateNotValid'), {
        toastId: 'noValidDate',
      });
      return false;
    }

    try {
      setIsDemandRequestOngoing(true);

      const payload = demandService.formatVehicleBasedDWDemandStateToDemand(
        filteredState,
        vehicleId,
        [...supportedServices],
        pricing,
        duration
      );

      if (submitType === 'default') {
        const res = await demandService.createDemand({
          ...payload,
          source: 'API',
        });

        analyticsService.addToCartVehicleBased({
          vehicleId,
          totalPrice: payload.paymentInfo?.totalAmount ?? 0,
          currency: payload.paymentInfo?.currency ?? Currency.TRY,
          duration: duration,
          hourlyPrice: payload.paymentInfo?.vehicleCostPerUnit ?? 0,
          region: regionTRName,
          vehicleName,
          vehicleType,
          vehiclePriceUnit: pricing?.priceUnit || VehiclePriceUnit.DAY,
        });

        if (res.offerId) {
          window.open(
            `${getLocalePrefix(locale)}payment/${res.offerId}`,
            '_self'
          );
        } else {
          toast.success(t('priceWidget.demandSuccess'), {
            toastId: 'demandSuccess',
          });
          if (isMessagingActive) {
            translateAndNavigate(`/message?demandId=${res.demandId}`);
          } else {
            translateAndNavigate('/tekliflerim');
          }
        }
      } else if (submitType === 'message') {
        const res = await demandService.createDemand({
          ...payload,
          initialMessage: message,
          source: 'MESSAGE',
        });

        analyticsService.addToCartVehicleBased({
          vehicleId,
          totalPrice: payload.paymentInfo?.totalAmount ?? 0,
          currency: payload.paymentInfo?.currency ?? Currency.TRY,
          duration: duration,
          hourlyPrice: payload.paymentInfo?.vehicleCostPerUnit ?? 0,
          region: regionTRName,
          vehicleName,
          vehicleType,
          vehiclePriceUnit: pricing?.priceUnit || VehiclePriceUnit.DAY,
        });

        router.push(`/message?demandId=${res.demandId}`);
      } else if (submitType === 'message-update' && demandId) {
        await demandService.updateDemand(demandId, {
          ...payload,
          source: 'MESSAGE',
        });
      }
      submitCallback?.();
    } finally {
      setIsDemandRequestOngoing(false);
    }
  };

  const formItemClasses = 'pb-2.5 pt-1 px-3';
  const formLabelClasses = 'text-sm font-semibold mb-1 inline-block';
  const formBorderColor = 'border-slate-500';

  return (
    <div>
      <div
        className={classNames(
          'form mt-3 grid grid-cols-2 rounded-2xl border',
          formBorderColor
        )}
      >
        <div
          className={classNames(
            formItemClasses,
            formBorderColor,
            'col-span-2 border-b'
          )}
        >
          <BDateRangePicker
            isRange={
              state.demandVariant !== SupportedDailyWeeklyRentalTypes.HALF_DAY
            }
            fixedHeight
            labelClasses={formLabelClasses}
            disabledDay={(date) =>
              offsetDays.includes(date) || getDisabledDays(date)
            }
            disabledDates={mappedReservedTimes}
            rangeLabels={{
              firstInput: t('priceWidget.form.checkInDate'),
              lastInput: t('priceWidget.form.checkOutDate'),
            }}
            nonRangeLabel={t('priceWidget.form.checkInDate')}
            ranges={[
              {
                startDate: state.startDate || new Date(),
                endDate: state.endDate || new Date(),
                key: 'state',
              },
            ]}
            onChange={(val) => {
              dispatch({
                type: 'startDate',
                payload: val.state.startDate!,
              });
              dispatch({
                type: 'endDate',
                payload: val.state.endDate!,
              });
            }}
          >
            <div className="px-2 pt-1 pb-2">
              <BSwitch
                value={
                  state.demandVariant ===
                  SupportedDailyWeeklyRentalTypes.HALF_DAY
                }
                disabled={!doesSupportHalfDayRental}
                labelClasses="text-info-600"
                onChange={(val) => {
                  dispatch({
                    type: 'REPLACE_STATE',
                    payload: {
                      ...state,
                      demandVariant: val
                        ? SupportedDailyWeeklyRentalTypes.HALF_DAY
                        : getDefaultDemandVariant(),
                      endDate: state.startDate,
                    },
                  });
                }}
                label={t('priceWidget.form.halfDay')}
              />
              {doesSupportHalfDayRental ? (
                <span className="mt-1 inline-block text-sm text-slate-500">
                  {t('priceWidget.form.halfDayInfo')}
                </span>
              ) : null}
              {doesSupportOnlyHalfDayRental ? (
                <span className="mt-1 inline-block text-sm text-slate-500">
                  {t('priceWidget.form.halfDayOnlySupported')}
                </span>
              ) : null}
              {!doesSupportHalfDayRental ? (
                <span className="mt-1 inline-block text-sm text-slate-500">
                  {t('priceWidget.form.halfDayNotSupported')}
                </span>
              ) : null}
              {}
            </div>
          </BDateRangePicker>
        </div>
        <div
          className={classNames(formItemClasses, formBorderColor, 'col-span-2')}
        >
          <label className={formLabelClasses} htmlFor="number-of-people">
            {t('priceWidget.form.personCount')}
          </label>
          <div className="relative">
            <BStepperInput
              inputProps={{ id: 'number-of-people' }}
              minLimit={MIN_PEOPLE}
              maxLimit={MAX_PEOPLE}
              value={state.numberOfPeople}
              inputEnabled
              boundaryErrorKey="vehicle:priceWidget.form.personCountBoundaryError"
              onChange={(value) =>
                dispatch({
                  type: 'numberOfPeople',
                  payload: `${value}`,
                })
              }
            />
          </div>
        </div>
      </div>
      <ModalWithActivator
        modalTitle={t('priceWidget.form.extrasModal.title')}
        activator={<PriceWidgetExtras />}
        modalClass="h-screen"
      >
        {extraTabs.length ? (
          <BTabs tabs={extraTabs} />
        ) : (
          <PriceWidgetExtrasEmptyView />
        )}
      </ModalWithActivator>
      {submitType === 'message' ? (
        <BTextArea
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          className="mt-4"
        />
      ) : null}
      {submitType === 'default' ? (
        <PriceWidgetCTAButton
          className="mt-4"
          fluid
          onClick={onSubmit}
          supportedDemandTypes={supportedDemandTypes}
          pricing={pricing}
          isCalculateAmountRequestSent={isCalculateAmountRequestSent}
          isCalcutePricingRequestOngoing={isCalcutePricingRequestOngoing}
          isDemandRequestOngoing={isDemandRequestOngoing}
        />
      ) : (
        <BButton
          className="mt-4"
          fluid
          onClick={onSubmit}
          disabled={isDemandRequestOngoing}
        >
          {isDemandRequestOngoing ? (
            <BSpinnerInline size={24} className="mx-auto" />
          ) : submitType === 'message' ? (
            t('message.cta')
          ) : (
            t('update.cta')
          )}
        </BButton>
      )}
    </div>
  );
};

export default PriceWidgetDailyWeeklyForm;
